import { useEffect, useMemo, useState } from "react";
import {
  PageSection,
  Card,
  Button,
  Spinner,
  InlineAlert,
  CheckboxField,
} from "@constructorfleet/ui";
import { CollectionSiteMap } from "./CollectionSiteMap";

type OpeningHourRecord = {
  day_of_week?: number;
  open_time?: string;
  close_time?: string;
};

export type CollectionSite = {
  id: string;
  name: string;
  address?: string;
  lat?: number;
  lng?: number;
  distance_meters?: number;
  opening_hours?: string | string[] | OpeningHourRecord[];
  approved?: boolean;
  open_now?: boolean;
};

type Props = {
  lat?: number;
  lng?: number;
  radius?: number;
  className?: string;
  items?: CollectionSite[];
};

const weekdayLabels = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function formatDistance(m?: number) {
  if (typeof m !== "number") return "";
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m)} m`;
}

function formatLocation(site: CollectionSite) {
  if (site.address) return site.address;
  if (typeof site.lat === "number" && typeof site.lng === "number") {
    return `${site.lat.toFixed(4)}, ${site.lng.toFixed(4)}`;
  }
  return "Location unavailable";
}

function formatOpeningHours(value: CollectionSite["opening_hours"]) {
  if (!value) return ["Hours unavailable"];
  if (typeof value === "string") return [value];
  if (!Array.isArray(value)) return ["Hours unavailable"];
  if (value.length === 0) return ["Hours unavailable"];
  if (typeof value[0] === "string") return value as string[];

  return (value as OpeningHourRecord[]).map((entry) => {
    const dayLabel =
      typeof entry.day_of_week === "number" && weekdayLabels[entry.day_of_week]
        ? weekdayLabels[entry.day_of_week]
        : "Day";
    const openTime = entry.open_time ?? "Unknown";
    const closeTime = entry.close_time ?? "Unknown";
    return `${dayLabel}: ${openTime} - ${closeTime}`;
  });
}

export function CollectionSites({ lat, lng, radius = 5000, className, items: staticItems }: Props) {
  const [sites, setSites] = useState<CollectionSite[]>(staticItems ?? []);
  const [loading, setLoading] = useState(staticItems == null);
  const [error, setError] = useState<string | null>(null);
  const [viewMap, setViewMap] = useState(false);
  const [onlyApproved, setOnlyApproved] = useState(true);
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(null);

  useEffect(() => {
    if (staticItems) {
      setSites(staticItems.filter((site) => (onlyApproved ? site.approved !== false : true)));
      setLoading(false);
      setError(null);
      return;
    }

    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams();
        if (typeof lat === "number" && typeof lng === "number") {
          q.set("lat", String(lat));
          q.set("lng", String(lng));
        }
        q.set("radius", String(radius));
        q.set("onlyApproved", String(onlyApproved));
        const url = `/api/website/collection-sites/nearby?${q.toString()}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!active) return;
        if (!res.ok) {
          setError("Unable to load collection sites");
          setSites([]);
          return;
        }
        const data = (await res.json()) as { sites?: unknown };
        if (Array.isArray(data?.sites)) {
          setSites(data.sites as CollectionSite[]);
        } else {
          setSites([]);
        }
      } catch {
        if (!active) return;
        setError("Unable to load collection sites");
        setSites([]);
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [lat, lng, radius, onlyApproved, staticItems]);

  useEffect(() => {
    setExpandedSiteId((current) =>
      current && sites.some((site) => site.id === current) ? current : null
    );
  }, [sites]);

  const hasPreciseLocation = useMemo(
    () => typeof lat === "number" && typeof lng === "number",
    [lat, lng]
  );

  return (
    <PageSection
      title="Collection sites"
      description="Approved nearby collection sites with full details kept in the same dashboard."
    >
      <div className={`cf-grid ${className || ""}`} style={{ gap: "0.75rem" }}>
        <Card>
          <div className="cf-grid" style={{ gap: "0.5rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}
              >
                <Button
                  type="button"
                  variant={viewMap ? "ghost" : "solid"}
                  onClick={() => setViewMap(false)}
                >
                  List
                </Button>
                <Button
                  type="button"
                  variant={viewMap ? "solid" : "ghost"}
                  onClick={() => setViewMap(true)}
                >
                  Map
                </Button>
                <CheckboxField
                  id="only-approved"
                  label="Approved only"
                  checked={onlyApproved}
                  onChange={(e) => setOnlyApproved((e.target as HTMLInputElement).checked)}
                />
              </div>
              <div style={{ alignSelf: "center" }}>
                <small aria-hidden>Showing {sites.length} sites</small>
              </div>
            </div>

            {loading ? (
              <div aria-label="loading-sites">
                <Spinner />
              </div>
            ) : error ? (
              <InlineAlert tone="danger">{error}</InlineAlert>
            ) : sites.length === 0 ? (
              <InlineAlert tone="neutral">No collection sites found nearby.</InlineAlert>
            ) : viewMap ? (
              <CollectionSiteMap sites={sites} centerLat={lat} centerLng={lng} />
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {sites.map((site) => {
                  const isExpanded = expandedSiteId === site.id;
                  const hours = formatOpeningHours(site.opening_hours);
                  const distanceLabel =
                    hasPreciseLocation && typeof site.distance_meters === "number"
                      ? formatDistance(site.distance_meters)
                      : null;

                  return (
                    <li key={site.id} style={{ marginBottom: "0.5rem" }}>
                      <article
                        className="cf-grid"
                        style={{
                          gap: "0.5rem",
                          padding: "0.5rem",
                          border: site.open_now
                            ? "2px solid var(--cf-success)"
                            : "1px solid var(--cf-border)",
                          borderRadius: 6,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <div className="cf-grid" style={{ gap: "0.2rem", flex: "1 1 18rem" }}>
                            <button
                              type="button"
                              className="cf-btn cf-btn-sm cf-outline-neutral"
                              aria-expanded={isExpanded}
                              aria-controls={`collection-site-${site.id}`}
                              onClick={() =>
                                setExpandedSiteId((current) =>
                                  current === site.id ? null : site.id
                                )
                              }
                              style={{ justifySelf: "start" }}
                            >
                              {site.name}
                            </button>
                            {site.approved === false ? (
                              <p className="cf-help" style={{ margin: 0 }}>
                                Unapproved site
                              </p>
                            ) : null}
                            {site.open_now ? (
                              <div style={{ color: "var(--cf-success)", fontWeight: 600 }}>
                                Open now
                              </div>
                            ) : null}
                          </div>
                          <div style={{ textAlign: "right" }}>
                            {distanceLabel ? <div>{distanceLabel}</div> : null}
                            <div className="cf-help">
                              {isExpanded ? "Hide details" : "Show details"}
                            </div>
                          </div>
                        </div>

                        {isExpanded ? (
                          <div
                            id={`collection-site-${site.id}`}
                            style={{
                              display: "grid",
                              gap: "0.75rem",
                              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                              borderTop: "1px solid var(--cf-color-border)",
                              paddingTop: "0.75rem",
                            }}
                          >
                            <div>
                              <p className="cf-help" style={{ margin: 0 }}>
                                Location
                              </p>
                              <p style={{ margin: 0 }}>{formatLocation(site)}</p>
                            </div>
                            <div>
                              <p className="cf-help" style={{ margin: 0 }}>
                                Approval
                              </p>
                              <p style={{ margin: 0 }}>
                                {site.approved === false ? "Not approved" : "Approved"}
                              </p>
                            </div>
                            <div style={{ gridColumn: "1 / -1" }}>
                              <p className="cf-help" style={{ margin: 0 }}>
                                Hours
                              </p>
                              <div className="cf-grid" style={{ gap: "0.25rem" }}>
                                {hours.map((line) => (
                                  <p key={line} style={{ margin: 0 }}>
                                    {line}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </PageSection>
  );
}
